export const DEFAULT_ELO = 1200;
export const ELO_PROVISIONAL_GAMES = 10;
export const FAST_MAX_MINUTES = 3;

export type EloPool = 'fast' | 'standard';

export function getEloPool(minutesPerPlayer?: number | null): EloPool | null {
  const mins = Number(minutesPerPlayer);
  if (!Number.isFinite(mins) || mins <= 0) return null;
  return mins <= FAST_MAX_MINUTES ? 'fast' : 'standard';
}

export function eloPoolLabel(pool: EloPool | null | undefined): string {
  if (pool === 'fast') return 'Fast';
  if (pool === 'standard') return 'Standard';
  return '';
}

export function getTournamentSeedPool(
  roundSettings: Array<{ minutes_per_player?: number; minutesPerPlayer?: number }> | null | undefined
): EloPool {
  const settings = roundSettings || [];
  const pools = settings
    .map(s => getEloPool(s.minutes_per_player != null ? s.minutes_per_player : s.minutesPerPlayer))
    .filter((p): p is EloPool => !!p);
  if (pools.length === 0) return 'standard';
  return pools.every(p => p === 'fast') ? 'fast' : 'standard';
}

export function formatRatedClockLabel(
  minutesPerPlayer?: number | null,
  incrementSeconds?: number | null,
  isRated?: boolean
): string {
  const pool = getEloPool(minutesPerPlayer);
  const ratedWord = isRated ? 'rated' : 'casual';
  if (!pool || minutesPerPlayer == null) {
    return isRated ? 'Rated (untimed — will not count)' : 'Casual';
  }
  const inc = incrementSeconds && incrementSeconds > 0 ? `+${incrementSeconds}` : '';
  return `${eloPoolLabel(pool)} ${ratedWord} ${minutesPerPlayer}${inc}`;
}

export function isEloProvisional(ratedGames?: number | null): boolean {
  return (ratedGames ?? 0) < ELO_PROVISIONAL_GAMES;
}

export function formatEloNumber(rating?: number | null, ratedGames?: number | null): string {
  const value = Math.round(rating ?? DEFAULT_ELO);
  return isEloProvisional(ratedGames) ? `${value}?` : `${value}`;
}

export function provisionalEloTitle(pool: EloPool): string {
  const label = eloPoolLabel(pool);
  return `Provisional — not established yet. This ${label} rating drops the ? after 10 rated ${label} games.`;
}

export function eloNumberTitle(pool: EloPool, ratedGames?: number | null): string | undefined {
  return isEloProvisional(ratedGames) ? provisionalEloTitle(pool) : undefined;
}

export function formatEloChange(change?: number | null): string {
  const n = Math.round(change ?? 0);
  return n > 0 ? `+${n}` : `${n}`;
}

export function formatEloResultLine(
  name: string,
  change?: number | null,
  newRating?: number | null,
  isProvisional?: boolean
): string {
  const rating = Math.round(newRating ?? DEFAULT_ELO);
  return `${name} ${formatEloChange(change)} → ${rating}${isProvisional ? '?' : ''}`;
}
